import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

import enAnalytics from "./locales/en/analytics.json";
import enApps from "./locales/en/apps.json";
import enAuth from "./locales/en/auth.json";
import enChat from "./locales/en/chat.json";
import enCommon from "./locales/en/common.json";
import enDashboard from "./locales/en/dashboard.json";
import enDocuments from "./locales/en/documents.json";
import enReports from "./locales/en/reports.json";
import enIntelligence from "./locales/en/intelligence.json";
import enLeads from "./locales/en/leads.json";
import enNav from "./locales/en/nav.json";
import enOnboarding from "./locales/en/onboarding.json";
import enProfile from "./locales/en/profile.json";
import enRecruit from "./locales/en/recruit.json";
import enSettings from "./locales/en/settings.json";
import enValidation from "./locales/en/validation.json";
import svAnalytics from "./locales/sv/analytics.json";
import svApps from "./locales/sv/apps.json";
import svAuth from "./locales/sv/auth.json";
import svChat from "./locales/sv/chat.json";
import svCommon from "./locales/sv/common.json";
import svDashboard from "./locales/sv/dashboard.json";
import svDocuments from "./locales/sv/documents.json";
import svReports from "./locales/sv/reports.json";
import svIntelligence from "./locales/sv/intelligence.json";
import svLeads from "./locales/sv/leads.json";
import svNav from "./locales/sv/nav.json";
import svOnboarding from "./locales/sv/onboarding.json";
import svProfile from "./locales/sv/profile.json";
import svRecruit from "./locales/sv/recruit.json";
import svSettings from "./locales/sv/settings.json";
import svValidation from "./locales/sv/validation.json";

/** Languages the app ships translations for. */
export const SUPPORTED_LANGUAGES = ["sv", "en"] as const;
export type AppLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/** localStorage key for the user's chosen UI language (also used as the detector cache). */
export const GRAFTUS_LANGUAGE_KEY = "graftus_language";

/** Translation namespaces, one file per feature area. */
export const NAMESPACES = [
  "common",
  "dashboard",
  "nav",
  "onboarding",
  "auth",
  "apps",
  "recruit",
  "leads",
  "intelligence",
  "chat",
  "settings",
  "profile",
  "documents",
  "reports",
  "analytics",
  "validation",
] as const;

export const DEFAULT_NAMESPACE = "common";

export const resources = {
  sv: {
    common: svCommon,
    dashboard: svDashboard,
    nav: svNav,
    onboarding: svOnboarding,
    auth: svAuth,
    apps: svApps,
    recruit: svRecruit,
    leads: svLeads,
    intelligence: svIntelligence,
    chat: svChat,
    settings: svSettings,
    profile: svProfile,
    documents: svDocuments,
    reports: svReports,
    analytics: svAnalytics,
    validation: svValidation,
  },
  en: {
    common: enCommon,
    dashboard: enDashboard,
    nav: enNav,
    onboarding: enOnboarding,
    auth: enAuth,
    apps: enApps,
    recruit: enRecruit,
    leads: enLeads,
    intelligence: enIntelligence,
    chat: enChat,
    settings: enSettings,
    profile: enProfile,
    documents: enDocuments,
    reports: enReports,
    analytics: enAnalytics,
    validation: enValidation,
  },
} as const;

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: "sv",
    supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
    // Map regional variants (e.g. en-US, en-GB) onto our base languages.
    load: "languageOnly",
    nonExplicitSupportedLngs: true,
    ns: NAMESPACES as unknown as string[],
    defaultNS: DEFAULT_NAMESPACE,
    detection: {
      // Prefer the user's saved choice; otherwise fall back to browser language.
      order: ["localStorage", "navigator"],
      lookupLocalStorage: GRAFTUS_LANGUAGE_KEY,
      caches: ["localStorage"],
    },
    interpolation: {
      // React already escapes values, so no double-escaping needed.
      escapeValue: false,
    },
  });

// Keep the document <html lang> in sync so the browser/AT know the page language.
i18n.on("languageChanged", (lng) => {
  if (typeof document !== "undefined") {
    document.documentElement.lang = lng;
  }
});

export default i18n;
